import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface Annonce {
  id: string;
  message: string;
  actif: boolean;
  ordre: number;
  date_creation: string;
}

export default function AdminAnnoncesPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [annonces, setAnnonces] = useState<Annonce[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editMessage, setEditMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'super_admin' && role !== 'admin') { navigate('/', { replace: true }); return; }
    loadAnnonces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role, authLoading]);

  const loadAnnonces = async () => {
    try {
      const { data, error: fetchErr } = await supabase
        .from('annonces_defilantes')
        .select('*')
        .order('ordre', { ascending: true })
        .order('date_creation', { ascending: true });
      if (fetchErr) throw fetchErr;
      setAnnonces(data || []);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const addAnnonce = async () => {
    const msg = newMessage.trim();
    if (!msg) { setError('Le message est obligatoire'); return; }
    try {
      const nextOrdre = annonces.reduce((max, a) => Math.max(max, a.ordre), 0) + 1;
      const { error: insertErr } = await supabase
        .from('annonces_defilantes')
        .insert({ message: msg, actif: true, ordre: nextOrdre });
      if (insertErr) throw insertErr;
      setSuccess('Message ajouté');
      setNewMessage('');
      loadAnnonces();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const toggleActif = async (id: string, actif: boolean) => {
    await supabase.from('annonces_defilantes').update({ actif: !actif }).eq('id', id);
    loadAnnonces();
  };

  const startEdit = (a: Annonce) => {
    setEditing(a.id);
    setEditMessage(a.message);
  };

  const saveEdit = async () => {
    if (!editMessage.trim()) return;
    await supabase.from('annonces_defilantes').update({ message: editMessage.trim() }).eq('id', editing!);
    setEditing(null);
    setEditMessage('');
    loadAnnonces();
    setSuccess('Message modifié');
    setTimeout(() => setSuccess(''), 3000);
  };

  const deleteAnnonce = async (id: string) => {
    if (!confirm('Supprimer ce message ?')) return;
    await supabase.from('annonces_defilantes').delete().eq('id', id);
    loadAnnonces();
    setSuccess('Message supprimé');
    setTimeout(() => setSuccess(''), 3000);
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= annonces.length) return;
    const a = annonces[index];
    const b = annonces[target];
    await supabase.from('annonces_defilantes').update({ ordre: b.ordre }).eq('id', a.id);
    await supabase.from('annonces_defilantes').update({ ordre: a.ordre }).eq('id', b.id);
    loadAnnonces();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4 block"></i>
          <p className="text-clinic-blue-dark">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-xl border-2 border-clinic-blue/15 overflow-hidden">
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-clinic-blue-dark">Bandeau d'annonces</h1>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">
                  Gérez les messages qui défilent sur la page d'accueil de la plateforme
                </p>
              </div>
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                ← Retour
              </button>
            </div>

            <p className="text-xs text-gray-400 mb-8 flex items-start gap-2">
              <i className="ri-information-line mt-0.5"></i>
              <span>Ces messages s'affichent dans le bandeau défilant sous le bandeau principal de la page d'accueil, à côté du logo de la clinique.</span>
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-2xl flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-600 rounded-2xl flex items-start gap-2">
                <i className="ri-check-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                <span>{success}</span>
              </div>
            )}

            {/* Formulaire d'ajout */}
            <div className="mb-8 p-6 bg-clinic-blue/5 border-2 border-clinic-blue/20 rounded-2xl">
              <h3 className="text-lg font-semibold text-clinic-blue-dark mb-4">Ajouter un message</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addAnnonce(); }}
                  placeholder="Écrivez votre message d'information..."
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
                <button
                  onClick={addAnnonce}
                  className="px-6 py-3 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark font-semibold whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-add-line mr-1"></i>
                  Ajouter
                </button>
              </div>
            </div>

            {/* Liste */}
            {annonces.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <i className="ri-notification-line text-5xl block mb-3"></i>
                <p>Aucun message pour le moment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {annonces.map((a, index) => (
                  <div
                    key={a.id}
                    className={`p-4 rounded-xl border-2 transition-all ${a.actif ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}
                  >
                    {editing === a.id ? (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={editMessage}
                          onChange={(e) => setEditMessage(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); }}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-clinic-blue"
                        />
                        <button onClick={saveEdit} className="px-4 py-2 bg-clinic-blue text-white rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap">
                          <i className="ri-check-line mr-1"></i>Enregistrer
                        </button>
                        <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap">
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-0.5 flex-shrink-0 pt-0.5">
                          <button
                            onClick={() => move(index, -1)}
                            disabled={index === 0}
                            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-clinic-blue cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <i className="ri-arrow-up-s-line"></i>
                          </button>
                          <button
                            onClick={() => move(index, 1)}
                            disabled={index === annonces.length - 1}
                            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-clinic-blue cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <i className="ri-arrow-down-s-line"></i>
                          </button>
                        </div>
                        <p className="flex-1 text-gray-800 leading-relaxed text-sm sm:text-base">{a.message}</p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => toggleActif(a.id, a.actif)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap ${a.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                          >
                            {a.actif ? 'Actif' : 'Inactif'}
                          </button>
                          <button onClick={() => startEdit(a)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-clinic-blue cursor-pointer">
                            <i className="ri-edit-line"></i>
                          </button>
                          <button onClick={() => deleteAnnonce(a.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}